import { assertNever } from "@theprelude/core";
import {
  AdditiveOpNode,
  AllNode,
  AndExprNode,
  AndOpNode,
  AnyNode,
  ArithmeticExprNode,
  BoolCommonExprNode,
  BooleanLiteralNode,
  CloseParenNode,
  CommaNode,
  CommonExprNode,
  ComparisonExprNode,
  ComparisonOpNode,
  DurationLiteralNode,
  IdentifierNode,
  LambdaExprNode,
  LambdaPredicateNode,
  ListItemsNode,
  lex,
  MemberExprNode,
  MethodCallNode,
  MethodNameNode,
  MultiplicativeExprNode,
  NullLiteralNode,
  NumberLiteralNode,
  ODataNode,
  OpenParenNode,
  OrExprNode,
  OrOpNode,
  ParenOrListExprNode,
  PrimaryExprNode,
  parse,
  StringLiteralNode,
  UnaryExprNode,
  UnaryOpNode,
  ValueLiteralNode,
} from "@theprelude/odata-parser";
import { z } from "zod";
import {
  flipOperator,
  getOdataDurationString,
} from "@/components/filter-form/available-filters";
import {
  Filter,
  filterSchema,
  ObjectFilter,
  OdataOperator,
  OdataPropertyFn,
  OdataScope,
  OdataValue,
  OdataWith,
  odataPropertyFns,
  odataScopes,
  odataWithSchema,
} from "./filter";

export function getEncodedOdataValue(
  value: string | number | boolean | null,
): string {
  if (typeof value === "string") {
    return `'${value.replaceAll("'", "''")}'`;
  }
  if (typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return "null";
}

export function isDurationFilter(filter: Filter): filter is ObjectFilter {
  return (
    typeof filter.value === "object" &&
    filter.value !== null &&
    filter.value.type === "raw" &&
    filter.value.value.includes("now() sub duration")
  );
}

export function parseFilter(source: string): Filter[] {
  const filters = new FilterParser().parse(source);
  return z.array(filterSchema).parse(filters);
}

function hasNodePattern<TNodeTypes extends readonly ODataNodeType[]>(
  children: ODataNode[],
  ...nodeTypes: TNodeTypes
): children is ODataNode[] & {
  [K in keyof TNodeTypes]: ODataNode & { type: TNodeTypes[K] };
} {
  return (
    nodeTypes.length === children.length &&
    children.every((child, index) => nodeTypes[index] === child.type)
  );
}

type ODataNodeType = ODataNode["type"];

class FilterParser {
  parse(source: string): Filter[] {
    const tokens = lex(source);
    const ast = parse(tokens);
    return this.visitBoolCommonExpr(ast);
  }

  private visitBoolCommonExpr(node: BoolCommonExprNode): Filter[] {
    const root = this.visitCommonExpr(node.children[0]);
    const reducedRoot = wrapConjunction(reduceConjunctions(root));
    const filters = this.buildFilters(reducedRoot);
    return filters;
  }

  private visitCommonExpr({ children }: CommonExprNode): ParsedConjunction {
    if (hasNodePattern(children, "orExpr")) {
      return this.visitOrExpr(children[0]);
    }

    const left = this.visitCommonExpr(children[0]);
    const op = this.visitConjunction(children[1]);
    const right = this.visitOrExpr(children[2]);

    return this.join(op, left, right);
  }

  private visitOrExpr({ children }: OrExprNode): ParsedConjunction {
    if (hasNodePattern(children, "andExpr")) {
      return this.visitAndExpr(children[0]);
    }

    const left = this.visitOrExpr(children[0]);
    const op = this.visitConjunction(children[1]);
    const right = this.visitAndExpr(children[2]);

    return this.join(op, left, right);
  }

  private visitConjunction(node: AndOpNode | OrOpNode): Conjunction {
    return node.value;
  }

  private visitAndExpr({ children }: AndExprNode): ParsedConjunction {
    if (hasNodePattern(children, "comparisonExpr")) {
      const child = this.visitComparisonExpr(children[0]);
      if (child.type === "conjunction") {
        return child;
      }
      return { type: "conjunction", conjunction: "and", children: [child] };
    }

    const left = this.visitAndExpr(children[0]);
    const op = this.visitConjunction(children[1]);
    const right = this.visitComparisonExpr(children[2]);

    if (right.type === "conjunction") {
      return this.join(op, left, right);
    }
    return { ...left, children: [...left.children, right] };
  }

  private compare(
    id: ParsedNode,
    operator: OdataOperator,
    value: ParsedNode,
  ): ParsedNode {
    if (
      value.type === "conjunction" &&
      value.children.length === 1 &&
      value.children[0] !== undefined
    ) {
      return this.compare(id, operator, value.children[0]);
    }

    if (value.type === "list") {
      const listOperator =
        operator === "contains"
          ? "is"
          : operator === "does not contain"
            ? "is not"
            : operator;

      return {
        type: "conjunction",
        conjunction: "or",
        children: value.items.map((item) =>
          this.compare(id, listOperator, item),
        ),
      };
    }

    if (value.type !== "value") {
      throw new Error(`Unexpected value: "${JSON.stringify(value)}"`);
    }
    if (id.type === "value" && id.value !== "string") {
      throw new Error(`Unexpected identifier: "${JSON.stringify(id)}"`);
    }

    if (id.type === "member") {
      return {
        type: "comparison",
        id: id.id,
        fn: id.member && isValidMethod(id.member) ? id.member : undefined,
        operator,
        value,
      };
    }

    if (id.type === "value") {
      return { type: "comparison", id: id.value, operator, value };
    }

    if (id.type === "method" && isValidMethod(id.method)) {
      const result = parseFnMethod(id);
      return {
        type: "comparison",
        id: result.id,
        fn: result.fn,
        operator,
        value,
      };
    }

    throw new Error(
      `Unexpected ${id.type} on left-hand side of comparison: ${JSON.stringify(id)}`,
    );
  }

  private visitComparisonExpr(node: ComparisonExprNode): ParsedNode {
    if (hasNodePattern(node.children, "arithmeticExpr")) {
      return this.visitArithmeticExpr(node.children[0]);
    }

    const id = this.visitArithmeticExpr(node.children[0]);
    const operator = this.visitComparisonOp(node.children[1]);
    const value = this.visitArithmeticExpr(node.children[2]);

    return this.compare(id, operator, value);
  }

  private visitComparisonOp(node: ComparisonOpNode): OdataOperator {
    switch (node.type) {
      case "eq": {
        return this.visitEq(node);
      }
      case "ge": {
        return this.visitGe(node);
      }
      case "gt": {
        return this.visitGt(node);
      }
      case "has": {
        return this.visitHas(node);
      }
      case "in": {
        return this.visitIn(node);
      }
      case "le": {
        return this.visitLe(node);
      }
      case "lt": {
        return this.visitLt(node);
      }
      case "ne": {
        return this.visitNe(node);
      }
      default: {
        return assertNever(node.type);
      }
    }
  }

  private visitArithmeticExpr(node: ArithmeticExprNode): ParsedNode {
    if (hasNodePattern(node.children, "multiplicativeExpr")) {
      return this.visitMultiplicativeExpr(node.children[0]);
    }

    const arithmetic = this.visitArithmeticExpr(node.children[0]);
    const op = this.visitAdditiveOpNode(node.children[1]);
    const multiplicative = this.visitMultiplicativeExpr(node.children[2]);

    if (isDurationAgo(arithmetic, op, multiplicative)) {
      return multiplicative;
    }

    throw new Error(`Unexpected arithmetic: "${JSON.stringify(arithmetic)}"`);
  }

  private visitListItems(node: ListItemsNode): ParsedNode[] {
    if (hasNodePattern(node.children, "commonExpr")) {
      return [this.visitCommonExpr(node.children[0])];
    }
    return [
      ...this.visitListItems(node.children[0]),
      this.visitCommonExpr(node.children[2]),
    ];
  }

  private visitMultiplicativeExpr({
    children,
  }: MultiplicativeExprNode): ParsedNode {
    if (hasNodePattern(children, "unaryExpr")) {
      return this.visitUnaryExpr(children[0]);
    }
    throw new Error("Cannot parse multiple multiplicativeExpr nodes");
  }

  private visitUnaryExpr(node: UnaryExprNode): ParsedNode {
    if (hasNodePattern(node.children, "primaryExpr")) {
      return this.visitPrimaryExpr(node.children[0]);
    }

    const op = this.visitUnaryOpExpr(node.children[0]);
    const expression = this.visitUnaryExpr(node.children[1]);

    if (op === "not") {
      return this.negate(expression);
    }

    throw new Error(`Cannot perform ${op} on: ${JSON.stringify(expression)}`);
  }

  private visitUnaryOpExpr(node: UnaryOpNode): Unary {
    return node.value;
  }

  private visitPrimaryExpr(node: PrimaryExprNode): ParsedNode {
    if (hasNodePattern(node.children, "valueLiteral")) {
      return this.visitValueLiteral(node.children[0]);
    }
    if (hasNodePattern(node.children, "memberExpr")) {
      return this.visitMemberExpr(node.children[0]);
    }
    if (hasNodePattern(node.children, "methodCall")) {
      return this.visitMethodCall(node.children[0]);
    }
    if (hasNodePattern(node.children, "parenOrListExpr")) {
      return this.visitParenOrListExpr(node.children[0]);
    }
    if (hasNodePattern(node.children, "lambdaExpr")) {
      return this.visitLambdaExpr(node.children[0]);
    }
    return assertNever(node.children);
  }

  private visitDurationLiteral(node: DurationLiteralNode): ParsedValue {
    return { type: "value", dataType: "duration", value: node.value };
  }

  private visitMemberExpr(node: MemberExprNode): ParsedMember {
    if (hasNodePattern(node.children, "identifier")) {
      return { type: "member", id: this.visitIdentifier(node.children[0]) };
    }
    const member = this.visitMemberExpr(node.children[0]);
    const identifier = this.visitIdentifier(node.children[2]);
    return { type: "member", id: identifier, member: member.id };
  }

  private visitMethodCall(node: MethodCallNode): ParsedMethodCall {
    const method = this.visitMethodName(node.children[0]);

    // no args
    if (hasArgCount(node.children, 0)) {
      return { type: "method", method, args: [] };
    }

    // one arg
    if (hasArgCount(node.children, 1)) {
      const arg = this.visitCommonExpr(node.children[2]);
      return { type: "method", method, args: [arg] };
    }

    // two args
    if (hasArgCount(node.children, 2)) {
      const arg1 = this.visitCommonExpr(node.children[2]);
      const arg2 = this.visitCommonExpr(node.children[4]);
      return { type: "method", method, args: [arg1, arg2] };
    }

    // three args
    if (hasArgCount(node.children, 3)) {
      const arg1 = this.visitCommonExpr(node.children[2]);
      const arg2 = this.visitCommonExpr(node.children[4]);
      const arg3 = this.visitCommonExpr(node.children[6]);
      return { type: "method", method, args: [arg1, arg2, arg3] };
    }

    return assertNever(node.children);
  }

  private visitLambdaExpr(node: LambdaExprNode): ParsedLambda {
    const [
      memberExpr,
      _slash,
      quantifierExpr,
      _lparen,
      lambdaPredicateExpr,
      _rparen,
    ] = node.children;

    const member = this.visitMemberExpr(memberExpr);
    const quantifier = this.visitQuantifier(quantifierExpr);
    const lambda = this.visitLambdaPredicate(lambdaPredicateExpr);

    return {
      type: "lambda",
      quantifier,
      member: member.id,
      predicate: lambda.predicate,
      expression: lambda.expression,
    };
  }

  private visitLambdaPredicate(node: LambdaPredicateNode): ParsedPredicate {
    const predicate = this.visitIdentifier(node.children[0]);
    const expression = this.visitCommonExpr(node.children[2]);

    return { type: "predicate", predicate, expression };
  }

  private visitMethodName(node: MethodNameNode) {
    return node.value;
  }

  private visitEq(_node: ComparisonOpNode): OdataOperator {
    return "is";
  }

  private visitNe(_node: ComparisonOpNode): OdataOperator {
    return "is not";
  }

  private visitGt(_node: ComparisonOpNode): OdataOperator {
    return "greater than";
  }

  private visitGe(node: ComparisonOpNode): OdataOperator {
    throw new Error(`Unhandled operator: "${node.type}"`);
  }

  private visitLt(_node: ComparisonOpNode): OdataOperator {
    return "less than";
  }

  private visitLe(node: ComparisonOpNode): OdataOperator {
    throw new Error(`Unhandled operator: "${node.type}"`);
  }

  private visitIn(_node: ComparisonOpNode): OdataOperator {
    return "contains";
  }

  private visitHas(_node: ComparisonOpNode): OdataOperator {
    return "contains";
  }

  private visitAdditiveOpNode(node: AdditiveOpNode): Additive {
    return node.value;
  }

  private visitQuantifier(node: AnyNode | AllNode): Quantifier {
    return node.value;
  }

  private visitValueLiteral(node: ValueLiteralNode): ParsedValue {
    const child = node.children[0];
    switch (child.type) {
      case "booleanLiteral": {
        return this.visitBooleanLiteral(child);
      }
      case "durationLiteral": {
        return this.visitDurationLiteral(child);
      }
      case "nullLiteral": {
        return this.visitNullLiteral(child);
      }
      case "numberLiteral": {
        return this.visitNumberLiteral(child);
      }
      case "stringLiteral": {
        return this.visitStringLiteral(child);
      }
      default: {
        return assertNever(child);
      }
    }
  }

  private visitStringLiteral(node: StringLiteralNode): ParsedValue {
    return { type: "value", dataType: "string", value: node.value };
  }

  private visitNumberLiteral(node: NumberLiteralNode): ParsedValue {
    return { type: "value", dataType: "number", value: node.value };
  }

  private visitBooleanLiteral(node: BooleanLiteralNode): ParsedValue {
    return { type: "value", dataType: "boolean", value: node.value };
  }

  private visitNullLiteral(node: NullLiteralNode): ParsedValue {
    return { type: "value", dataType: "null", value: node.value };
  }

  private visitParenOrListExpr(node: ParenOrListExprNode): ParsedNode {
    if (hasNodePattern(node.children, "(", "commonExpr", ")")) {
      return this.visitCommonExpr(node.children[1]);
    }
    if (
      hasNodePattern(node.children, "(", "listItems", ",", "commonExpr", ")")
    ) {
      const listItems = this.visitListItems(node.children[1]);
      const expression = this.visitCommonExpr(node.children[3]);
      return { type: "list", items: [...listItems, expression] };
    }
    if (hasNodePattern(node.children, "(", "listItems", ")")) {
      const listItems = this.visitListItems(node.children[1]);
      return { type: "list", items: listItems };
    }
    return { type: "list", items: [] };
  }

  private visitIdentifier(node: IdentifierNode): string {
    return node.value;
  }

  private join(
    conjunction: Conjunction,
    left: ParsedConjunction,
    right: ParsedConjunction,
  ): ParsedConjunction {
    // do not allow ANDs to be nested under ORs
    if (
      conjunction === "or" &&
      ((left.conjunction !== "or" && left.children.length > 1) ||
        (right.conjunction !== "or" && right.children.length > 1))
    ) {
      throw new Error(
        `Cannot join "${left.conjunction}" and "${right.conjunction}" filters`,
      );
    }

    const children: ParsedNode[] = [];

    if (left.conjunction === conjunction || left.children.length <= 1) {
      children.push(...left.children);
    } else {
      children.push(left);
    }

    if (right.conjunction === conjunction || right.children.length <= 1) {
      children.push(...right.children);
    } else {
      children.push(right);
    }

    return { type: "conjunction", conjunction, children };
  }

  private negate<T extends ParsedNode>(node: T): T {
    const negate = this.negate.bind(this);

    switch (node.type) {
      case "comparison": {
        return { ...node, operator: flipOperator(node.operator) };
      }
      case "conjunction": {
        return {
          ...node,
          conjunction: flipConjunction(node.conjunction),
          children: node.children.map(negate),
        };
      }
      case "lambda": {
        return { ...node, expression: negate(node.expression) };
      }
      case "predicate": {
        return { ...node, expression: negate(node.expression) };
      }
      case "value": {
        if (node.dataType === "boolean") {
          return { ...node, value: !node.value };
        }
        throw new Error("Cannot negate literal values");
      }
      case "list": {
        throw new Error("Cannot negate list expressions");
      }
      case "member": {
        throw new Error("Cannot negate member expressions");
      }
      case "method": {
        if (node.method === "matchespattern") {
          return { ...node, method: "not matchespattern" };
        }
        if (node.method === "contains") {
          return { ...node, method: "not contains" };
        }
        throw new Error("Cannot negate methods");
      }
      default: {
        return assertNever(node);
      }
    }
  }

  private buildFilters(
    conjunction: ParsedConjunction,
    context: ParseContext = {},
  ): Filter[] {
    const getOperator = (op: OdataOperator): OdataOperator =>
      context.isArray ? getArrayOperator(op, context.quantifier) : op;

    if (conjunction.conjunction === "or") {
      const filters = this.buildFilters(
        { ...conjunction, conjunction: "and" },
        context,
      );
      const [primaryFilter, ...orFilters] = filters;
      if (!primaryFilter) {
        return [];
      }
      // ensure all OR-conditions reference the same id & scope
      if (
        orFilters.some(
          (or) =>
            or.id !== primaryFilter.id ||
            or.scope !== primaryFilter.scope ||
            or.quantifier !== primaryFilter.quantifier ||
            or.fn !== primaryFilter.fn,
        )
      ) {
        throw new Error("Cannot parse OR-conditions for differing fields");
      }
      return [
        {
          ...primaryFilter,
          operator: getOperator(primaryFilter.operator),
          quantifier: primaryFilter.quantifier ?? context.quantifier,
          isArray: primaryFilter.isArray ?? context.isArray,
          scope: primaryFilter.scope ?? context.scope,
          ors:
            orFilters.length > 0
              ? orFilters.map((or) => ({
                  operator: or.operator,
                  value: or.value,
                }))
              : undefined,
        },
      ];
    }

    const withs: OdataWith = {};

    while (
      conjunction.children.length > 1 &&
      conjunction.children[0] !== undefined &&
      conjunction.children[0].type === "comparison" &&
      conjunction.children[0].operator === "is" &&
      conjunction.children[0].value.type === "value" &&
      isScopeKey(conjunction.children[0].id) &&
      withs[conjunction.children[0].id] === undefined
    ) {
      const scopeKey = conjunction.children[0].id;
      assignScopeKey(withs, scopeKey, conjunction.children[0].value.value);

      // remove the filter condition since it's now part of the `with` object
      conjunction.children.splice(0, 1);
    }

    return conjunction.children.reduce(
      (filters: Filter[], child: ParsedNode) => {
        switch (child.type) {
          case "comparison": {
            const operator = getOperator(child.operator);
            const [firstValue, ...remainingValues] = getOdataValues(
              child.value,
            );
            filters.push({
              id: getFilterId(child),
              fn: child.fn,
              operator,
              value: firstValue,
              ors:
                remainingValues.length > 0
                  ? remainingValues.map((v) => ({ operator, value: v }))
                  : undefined,
              with: Object.keys(withs).length > 0 ? withs : undefined,
              ...context,
            });
            break;
          }
          case "lambda": {
            const newScope = isValidScope(child.member)
              ? child.member
              : context.scope;
            const newIsArray =
              (!!child.member && !isValidScope(child.member)) || undefined;

            filters.push(
              ...this.buildFilters(child.expression, {
                isArray: newIsArray,
                scope: newScope,
                quantifier: newIsArray
                  ? (context.quantifier ?? child.quantifier)
                  : child.quantifier,
              }),
            );
            break;
          }
          case "conjunction": {
            if (child.conjunction === "and") {
              throw new Error("Cannot parse nested AND filters");
            }
            filters.push(...this.buildFilters(child, context));
            break;
          }
          case "method": {
            filters.push(parseMethod(child, context));
            break;
          }
          case "list":
          case "member":
          case "predicate":
          case "value": {
            // noop
            break;
          }
          default: {
            return assertNever(child);
          }
        }
        return filters;
      },
      [],
    );
  }
}

const isScopeKey = (str: string): str is keyof OdataWith =>
  Object.keys(odataWithSchema.shape).includes(str);

const assignScopeKey = (
  withs: OdataWith,
  scopeKey: keyof OdataWith,
  value: any,
) => {
  switch (scopeKey) {
    case "category": {
      withs[scopeKey] = Number(value) as OdataWith[typeof scopeKey];
      break;
    }
    case "control": {
      withs[scopeKey] = Number(value) as OdataWith[typeof scopeKey];
      break;
    }
    case "event": {
      withs[scopeKey] = Number(value) as OdataWith[typeof scopeKey];
      break;
    }
    case "instance_id": {
      withs[scopeKey] = String(value);
      break;
    }
    default: {
      assertNever(scopeKey);
    }
  }
};

function getOdataValues(value: ParsedNode): [OdataValue, ...OdataValue[]] {
  if (
    value.type === "list" &&
    value.items.every((i): i is ParsedValue => i.type === "value")
  ) {
    if (value.items.length === 0) {
      throw new Error("Cannot parse empty lists");
    }
    return value.items.map((v) => v.value) as [OdataValue, ...OdataValue[]];
  }

  if (value.type === "value") {
    if (value.dataType === "null") {
      return [null];
    }
    return [
      value.dataType === "duration"
        ? wrapDurationObject(value.value)
        : value.value,
    ];
  }

  throw new Error(`Unexpected value: "${JSON.stringify(value)}"`);
}

function wrapDurationObject(duration: string): OdataValue {
  return {
    type: "raw",
    value: `now() sub duration'${duration}'`,
  };
}

function isValidScope(str: string): str is OdataScope {
  return odataScopes.includes(str as any);
}

function isValidMethod(str: string): str is OdataPropertyFn {
  return odataPropertyFns.includes(str as any);
}

function isDurationAgo(
  arithmetic: ParsedNode,
  op: Additive,
  multiplicative: ParsedNode,
): boolean {
  return (
    arithmetic.type === "method" &&
    arithmetic.method === "now" &&
    arithmetic.args.length === 0 &&
    op === "sub" &&
    multiplicative.type === "value" &&
    multiplicative.dataType === "duration"
  );
}

function getFilterId(comparison: ParsedComparison): string {
  const isDurationValue =
    comparison.value.type === "value" &&
    comparison.value.dataType === "duration";

  const isDurationList =
    comparison.value.type === "list" &&
    comparison.value.items.every(
      (item) => item.type === "value" && item.dataType === "duration",
    );

  return isDurationValue || isDurationList
    ? `duration_${comparison.id}`
    : comparison.id;
}

function getArrayOperator(
  op: OdataOperator,
  quantifier: Quantifier = "any",
): OdataOperator {
  if (op === "is" && quantifier === "all") {
    return "is";
  }
  if (op === "is not" && quantifier === "all") {
    return "is not";
  }
  if (op === "is" && quantifier === "any") {
    return "contains";
  }
  if (op === "is not" && quantifier === "any") {
    return "does not contain";
  }
  return op;
}

function flipConjunction(conjunction: Conjunction): Conjunction {
  return conjunction === "and" ? "or" : "and";
}

function reduceConjunctions(node: ParsedNode): ParsedNode {
  switch (node.type) {
    case "conjunction": {
      const [child, ...rest] = node.children;
      if (child && rest.length === 0) {
        return reduceConjunctions(child);
      }
      return { ...node, children: node.children.map(reduceConjunctions) };
    }
    case "lambda": {
      return {
        ...node,
        expression: wrapConjunction(reduceConjunctions(node.expression)),
      };
    }
    case "list": {
      return { ...node, items: node.items.map(reduceConjunctions) };
    }
    case "method": {
      return { ...node, args: node.args.map(reduceConjunctions) };
    }
    case "predicate": {
      return {
        ...node,
        expression: wrapConjunction(reduceConjunctions(node.expression)),
      };
    }
    case "comparison":
    case "member":
    case "value": {
      return node;
    }
    default: {
      return assertNever(node);
    }
  }
}

function wrapConjunction(node: ParsedNode): ParsedConjunction {
  if (node.type === "conjunction") {
    return node;
  }
  return { type: "conjunction", conjunction: "and", children: [node] };
}

function hasArgCount<C extends 0 | 1 | 2 | 3>(
  children: MethodCallNode["children"],
  count: C,
): children is MethodCallChildren<C> {
  if (count === 0 && children[2].type === ")") {
    return true;
  }
  if (count === 1 && children[3]?.type === ")") {
    return true;
  }
  if (count === 2 && children[5]?.type === ")") {
    return true;
  }
  if (count === 3 && children[7]?.type === ")") {
    return true;
  }
  return false;
}

type Quantifier = "any" | "all";
type Conjunction = "and" | "or";
type Unary = "not" | "minus";
type Additive = "add" | "sub";

type ParsedConjunction = {
  type: "conjunction";
  conjunction: Conjunction;
  children: ParsedNode[];
};

type ParsedValue = { type: "value" } & (
  | { dataType: "string"; value: string }
  | { dataType: "number"; value: number }
  | { dataType: "boolean"; value: boolean }
  | { dataType: "duration"; value: string }
  | { dataType: "null"; value: null }
);

type ParsedPredicate = {
  type: "predicate";
  predicate: string;
  expression: ParsedConjunction;
};

type ParsedLambda = {
  type: "lambda";
  quantifier: Quantifier;
  member: string;
  predicate: string;
  expression: ParsedConjunction;
};

type ParsedMember = {
  type: "member";
  id: string;
  member?: string;
};

type ParsedComparison = {
  type: "comparison";
  id: string;
  operator: OdataOperator;
  value: ParsedValue | ParsedList;
  fn?: OdataPropertyFn;
};

type ParsedMethodCall = {
  type: "method";
  method: string;
  args: ParsedNode[];
};

type ParsedList = {
  type: "list";
  items: ParsedNode[];
};

type ParsedNode =
  | ParsedMethodCall
  | ParsedMember
  | ParsedComparison
  | ParsedLambda
  | ParsedPredicate
  | ParsedValue
  | ParsedList
  | ParsedConjunction;

type ParseContext = {
  isArray?: boolean;
  scope?: OdataScope;
  quantifier?: Quantifier;
};

type MethodCallChildren<Count extends number> = Count extends 3
  ? [
      MethodNameNode,
      OpenParenNode,
      CommonExprNode,
      CommaNode,
      CommonExprNode,
      CommaNode,
      CommonExprNode,
      CloseParenNode,
    ]
  : Count extends 2
    ? [
        MethodNameNode,
        OpenParenNode,
        CommonExprNode,
        CommaNode,
        CommonExprNode,
        CloseParenNode,
      ]
    : Count extends 1
      ? [MethodNameNode, OpenParenNode, CommonExprNode, CloseParenNode]
      : [MethodNameNode, OpenParenNode, CloseParenNode];

const methodSchema = z.object({
  type: z.literal("method"),
  method: z.enum([
    "contains",
    "not contains",
    "matchespattern",
    "not matchespattern",
  ]),
  args: z.tuple([
    z.object({ type: z.literal("member"), id: z.string() }),
    z.object({
      type: z.literal("value"),
      dataType: z.literal("string"),
      value: z.string(),
    }),
  ]),
});

function parseMethod(
  method: ParsedMethodCall,
  context: ParseContext = {},
): Filter {
  const result = methodSchema.safeParse(method);
  if (!result.success) {
    throw new Error(`Unhandled method: ${method.method}`);
  }
  const name = result.data.method;
  const id = result.data.args[0].id;
  const value = result.data.args[1].value;
  switch (name) {
    case "contains": {
      return { id, operator: "contains", value, ...context };
    }
    case "matchespattern": {
      return { id, operator: "matches", value, ...context };
    }
    case "not contains": {
      return { id, operator: "does not contain", value, ...context };
    }
    case "not matchespattern": {
      return { id, operator: "does not match", value, ...context };
    }
  }
}

const fnMethodSchema = z.object({
  type: z.literal("method"),
  method: z.literal("length"),
  args: z.tuple([z.object({ type: z.literal("member"), id: z.string() })]),
});

function parseFnMethod(method: ParsedMethodCall): {
  fn: OdataPropertyFn;
  id: string;
} {
  const result = fnMethodSchema.safeParse({
    ...method,
    args: method.args.map(reduceConjunctions),
  });

  if (!result.success) {
    throw new Error(`Unhandled property method: ${method.method}`);
  }
  return { fn: result.data.method, id: result.data.args[0].id };
}
